"use client";

import { printPosSaleReceipt } from "@/modules/merchant/presentation/lib/print-pos-sale-receipt";
import { usePosSaleDetailQuery } from "@/modules/merchant/presentation/api/queries";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import {
  saleStatusBadgeVariant,
  statusLabel,
} from "@/modules/merchant/presentation/ui/shop-pos/pos-sale-status";
import type { ShopCurrency } from "@/shared/lib/currency";
import { formatPrice } from "@/shared/lib/currency";
import { Badge, Button, cn, Loader, toast } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  History,
  Printer,
  Receipt,
} from "lucide-react";
import { useMemo } from "react";

const POS_SALES_SEARCH = {
  offset: 0,
  limit: 20,
  status: undefined,
  createdFrom: undefined,
  createdTo: undefined,
  saleNumber: undefined,
  customerUserId: undefined,
  customerSearch: undefined,
} as const;

export type ShopPosCheckoutSuccessPanelProps = {
  shopId: string;
  saleId: string;
  saleNumber: string;
  invoiceCurrency: ShopCurrency;
  paidAmount: number;
  checkoutOutcome: {
    saleStatus: "paid" | "partial";
    balanceDue: number;
  };
  onDone: () => void;
};

export function ShopPosCheckoutSuccessPanel({
  shopId,
  saleId,
  saleNumber,
  invoiceCurrency,
  paidAmount,
  checkoutOutcome,
  onDone,
}: ShopPosCheckoutSuccessPanelProps) {
  const { currentShop } = useMerchantShops(shopId);
  const detailQ = usePosSaleDetailQuery(shopId, saleId, true);

  const isPartial = checkoutOutcome.saleStatus === "partial";
  const totalShown =
    detailQ.data?.total ??
    (isPartial
      ? paidAmount + checkoutOutcome.balanceDue
      : paidAmount);

  const receiptContext = useMemo(
    () =>
      currentShop
        ? {
            shopName: currentShop.shopName,
            branchName:
              detailQ.data?.branchName ?? currentShop.branchName ?? null,
          }
        : undefined,
    [currentShop, detailQ.data?.branchName],
  );

  const handlePrint = (paper: "thermal80" | "a4") => {
    const detail = detailQ.data;
    if (!detail) {
      toast.error("ກຳລັງໂຫຼດບິນ… ລອງໃໝ່ອີກຄັ້ງ");
      return;
    }
    printPosSaleReceipt(detail, receiptContext, {
      paper,
      template: "simple",
    });
  };

  const printPending = detailQ.isLoading || detailQ.isFetching;

  return (
    <div className="overflow-hidden">
      <div
        className={cn(
          "border-b px-5 py-6 text-center sm:px-6",
          isPartial
            ? "border-secondary/25 bg-gradient-to-br from-secondary/10 via-card to-muted/20"
            : "border-emerald-500/20 bg-gradient-to-br from-emerald-500/12 via-card to-muted/20",
        )}
      >
        <div
          className={cn(
            "mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl ring-4",
            isPartial
              ? "bg-secondary/15 text-secondary ring-secondary/20"
              : "bg-emerald-500/15 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400",
          )}
        >
          <CheckCircle2 className="size-9" aria-hidden strokeWidth={2} />
        </div>
        <h2 className="font-bold font-heading text-foreground text-xl uppercase tracking-tight sm:text-2xl">
          {isPartial ? "ບັນທຶກມັດຈຳແລ້ວ" : "ຊຳລະສຳເລັດ"}
        </h2>
        <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
          {isPartial
            ? "ບິນຖືກບັນທຶກແລ້ວ — ຊຳລະສ່ວນທີ່ເຫຼືອໄດ້ຈາກປະຫວັດບິນ"
            : "ການຂາຍສຳເລັດ — ພິມໃບບິນໃຫ້ລູກຄ້າໄດ້ທັນທີ"}
        </p>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-6">
        <div
          className="overflow-hidden rounded-xl border border-border shadow-sm"
          style={{
            backgroundImage:
              "linear-gradient(135deg, color-mix(in oklab, var(--foreground) 4%, transparent) 0%, transparent 55%)",
          }}
        >
          <div className="flex items-center gap-2 border-border border-b bg-muted/30 px-4 py-2.5">
            <Receipt
              className="size-4 shrink-0 text-[hsl(210_90%_48%)]"
              aria-hidden
            />
            <span className="font-heading font-semibold text-foreground text-xs uppercase tracking-wide">
              ສະຫຼຸບບິນ
            </span>
            <Badge
              variant={saleStatusBadgeVariant(checkoutOutcome.saleStatus)}
              className="ms-auto text-[10px]"
            >
              {statusLabel(checkoutOutcome.saleStatus)}
            </Badge>
          </div>

          <div className="space-y-3 px-4 py-4">
            <div>
              <p className="text-muted-foreground text-xs">ເລກບິນ</p>
              <p className="mt-0.5 font-bold font-heading text-3xl text-foreground tabular-nums tracking-tight">
                #{saleNumber}
              </p>
            </div>

            <div className="space-y-2 border-border border-t pt-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">
                  {isPartial ? "ຍອດບິນ" : "ຍອດຊຳລະ"}
                </span>
                <span className="font-bold font-heading text-foreground text-lg tabular-nums">
                  {formatPrice(totalShown, invoiceCurrency)}
                </span>
              </div>
              {isPartial ? (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">ຮັບແລ້ວ</span>
                    <span className="font-semibold text-emerald-700 tabular-nums dark:text-emerald-400">
                      {formatPrice(paidAmount, invoiceCurrency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-secondary/30 bg-secondary/5 px-3 py-2">
                    <span className="font-medium text-card-foreground">
                      ຍອດຄ້າງ
                    </span>
                    <span className="font-bold font-heading text-secondary text-lg tabular-nums">
                      {formatPrice(
                        checkoutOutcome.balanceDue,
                        invoiceCurrency,
                      )}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            className="h-12 w-full gap-2 font-bold text-base"
            disabled={printPending}
            onClick={() => handlePrint("thermal80")}
          >
            {printPending ? (
              <Loader />
            ) : (
              <Printer className="size-5 shrink-0" aria-hidden />
            )}
            ພິມໃບບິນ (80&nbsp;mm)
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10 w-full gap-2 font-medium"
            disabled={printPending}
            onClick={() => handlePrint("a4")}
          >
            <Printer className="size-4 shrink-0" aria-hidden />
            ພິມ A4
          </Button>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            to="/app/shops/$shopId/pos/sales"
            params={{ shopId }}
            search={POS_SALES_SEARCH}
            className={cn(
              "inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border px-4 font-medium text-sm transition-colors",
              "border-sky-500/35 bg-sky-500/10 text-sky-950 hover:bg-sky-500/15 dark:text-sky-100",
            )}
          >
            <History className="size-4 shrink-0" aria-hidden />
            ປະຫວັດບິນ
          </Link>
          <Button
            type="button"
            variant="outline"
            className="h-10 flex-1 font-semibold"
            onClick={onDone}
          >
            ປິດ · ຂາຍຕໍ່
          </Button>
        </div>
      </div>
    </div>
  );
}
